import { MTN_REGEX_AS_STRING } from "./manifestTrackingNumbers";

export type BolRule = {
  description: string;
  /** Value of WasteManifest.vendorSource the pipeline writes. */
  vendorSource: string;
  /** Regex source applied case-insensitively to the MTN. Null means every row. */
  pattern: string | null;
  /** True when a row is a bill of lading because the MTN does NOT match `pattern`. */
  negate: boolean;
};

const CLEAN_HARBORS: BolRule = {
  description: "MTN starts with BOL",
  vendorSource: "Clean Harbors",
  pattern: "^BOL",
  negate: false,
};

const UPS_VEOLIA: BolRule = {
  description: "MTN fails the e-Manifest MTN pattern",
  vendorSource: "Veolia",
  pattern: MTN_REGEX_AS_STRING,
  negate: true,
};

const STERICYCLE: BolRule = {
  description: "every vendor row",
  vendorSource: "Stericycle",
  pattern: null,
  negate: false,
};

/** Pipeline.name is a slug in most writers but a display name in some contexts. */
export function normalizePipelineName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Null when the pipeline has no BOL rule (including safety-kleen, whose signal is not stored). */
export function ruleForPipeline(pipelineName: string): BolRule | null {
  const name = normalizePipelineName(pipelineName);
  if (name.endsWith("cleanharbors")) return CLEAN_HARBORS;
  if (name === "upsveolia") return UPS_VEOLIA;
  if (name === "aspirusstericycle" || name === "phoenixchildrensstericycle") return STERICYCLE;
  return null;
}

/** Whether `mtn` is a bill of lading under `rule`, assuming the row is a Vendor row of the rule's vendorSource. */
export function ruleMatches(rule: BolRule, mtn: string): boolean {
  if (rule.pattern === null) return true;
  return new RegExp(rule.pattern, "i").test(mtn) !== rule.negate;
}

/** Whether `mtn` would be marked BILL_OF_LADING, assuming the row is a Vendor row of the rule's vendorSource. */
export function classifyBolRule(pipelineName: string, mtn: string): boolean {
  const rule = ruleForPipeline(pipelineName);
  return rule ? ruleMatches(rule, mtn) : false;
}

/** Bill rules keyed by the vendorSource their pipelines write; pipelines without a rule are skipped. */
export function rulesByVendorSource(pipelineNames: string[]): Map<string, BolRule> {
  const rules = new Map<string, BolRule>();
  for (const name of pipelineNames) {
    const rule = ruleForPipeline(name);
    if (rule) rules.set(rule.vendorSource, rule);
  }
  return rules;
}

export const CONTAINERS_MESSAGE = "Containers must be a whole number greater than 0";
export const QUANTITY_MESSAGE = "Quantity must be a number greater than 0";

/** A line's container count: a whole number of at least 1. */
export function isValidContainerCount(n: number): boolean {
  return Number.isInteger(n) && n >= 1;
}

/** A line's quantity: above 0. */
export function isValidLineQuantity(n: number): boolean {
  return n > 0;
}

/** The messages for a line's numbers; a null (blank) value passes. */
export function lineNumberMessages(line: {
  containerCount?: number | null;
  quantity?: number | null;
}): string[] {
  const messages: string[] = [];
  if (line.containerCount != null && !isValidContainerCount(line.containerCount)) {
    messages.push(CONTAINERS_MESSAGE);
  }
  if (line.quantity != null && !isValidLineQuantity(line.quantity)) {
    messages.push(QUANTITY_MESSAGE);
  }
  return messages;
}
